import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';

interface RecordData {
  single?: number;
  average?: number;
}

interface NationalRecord {
  event: string;
  eventName: string;
  singleTime: string;
  averageTime: string;
}

const EVENT_NAMES: Record<string, string> = {
  '333': '3x3x3 Cube',
  '222': '2x2x2 Cube',
  '444': '4x4x4 Cube',
  '555': '5x5x5 Cube',
  '666': '6x6x6 Cube',
  '777': '7x7x7 Cube',
  '333bf': '3x3x3 Blindfolded',
  '333fm': '3x3x3 Fewest Moves',
  '333oh': '3x3x3 One-Handed',
  'clock': 'Clock',
  'minx': 'Megaminx',
  'pyram': 'Pyraminx',
  'skewb': 'Skewb',
  'sq1': 'Square-1',
  '444bf': '4x4x4 Blindfolded',
  '555bf': '5x5x5 Blindfolded',
  '333mbf': '3x3x3 Multi-Blind',
};

const EVENT_ORDER = [
  '333', '222', '444', '555', '666', '777',
  '333bf', '333fm', '333oh', 'clock', 'minx',
  'pyram', 'skewb', 'sq1', '444bf', '555bf', '333mbf'
];

export default function Records() {
  const { t } = useTranslation();
  const [records, setRecords] = useState<NationalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatTime = (centiseconds: number, event: string): string => {
    if (event === '333fm') {
      // FMC is in moves, but average is stored as centiseconds (mean of 3 * 100)
      if (centiseconds >= 100) {
        return (centiseconds / 100).toFixed(2);
      }
      return centiseconds.toString();
    }

    if (event === '333mbf') {
      // Multi-blind format: DDTTTTTMM
      const decoded = centiseconds;
      const difference = 99 - Math.floor(decoded / 10000000);
      const time = Math.floor((decoded % 10000000) / 100);
      const missed = decoded % 100;
      const solved = difference + missed;
      const attempted = solved + missed;
      const minutes = Math.floor(time / 60);
      const seconds = time % 60;
      return `${solved}/${attempted} ${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    const totalSeconds = centiseconds / 100;

    if (totalSeconds >= 60) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = (totalSeconds % 60).toFixed(2);
      return `${minutes}:${parseFloat(seconds) < 10 ? '0' : ''}${seconds}`;
    }

    return totalSeconds.toFixed(2);
  };

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        const response = await fetch(
          'https://www.worldcubeassociation.org/api/v0/records'
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        const greeceRecords: Record<string, RecordData> = data.national_records?.Greece || {};

        if (Object.keys(greeceRecords).length === 0) {
          throw new Error('No records found');
        }

        // Convert to array and sort by event order
        const recordsArray: NationalRecord[] = EVENT_ORDER
          .filter(event => greeceRecords[event])
          .map(event => ({
            event,
            eventName: EVENT_NAMES[event] || event,
            singleTime: greeceRecords[event].single
              ? formatTime(greeceRecords[event].single!, event)
              : '-',
            averageTime: greeceRecords[event].average
              ? formatTime(greeceRecords[event].average!, event)
              : '-',
          }));

        setRecords(recordsArray);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, []);

  return (
    <div className="bg-gray-50 min-h-[70vh]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
          {t('records.title')}
        </h1>
        <p className="text-gray-600 mb-8">
          {t('records.subtitle')}
        </p>

        {loading && (
          <div className="flex flex-col justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-500">{t('records.loading')}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {t('records.error')}: {error}
          </div>
        )}

        {!loading && !error && records.length > 0 && (
          <div className="overflow-x-auto bg-white rounded-xl shadow-md">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('records.table.event')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('records.table.single')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('records.table.average')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((record) => (
                  <tr key={record.event} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="font-medium text-gray-900">{record.eventName}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-semibold text-blue-600">{record.singleTime}</span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-lg font-semibold text-green-600">{record.averageTime}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-8 text-center">
          <a
            href="https://www.worldcubeassociation.org/results/records?region=Greece"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('records.viewFull')}
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="mt-4 text-sm text-gray-500">
            {t('records.poweredBy')}{' '}
            <a
              href="https://www.worldcubeassociation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              World Cube Association
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
